// Iter-94 — Shared formatters + labels for the category-specific `details`
// dict on `lender_rates`. Used by:
//   • PartnerRateTable's CategoryDetailGlance + CategoryDetailsList
//   • CompareDrawer's modal spec rows (so 3 stashed lenders compare
//     against the same field labels + formatting they saw in the table)
//
// Keep this file as the single source of truth for detail rendering so
// future fields ship to both surfaces from one place.

export const DETAILS_LABELS = {
  coverage_type: "Coverage",
  avg_monthly_premium: "Avg premium",
  deductible_default: "Deductible",
  claim_satisfaction_score: "Claim satisfaction",
  driver_fit: "Best for",
  lender_type: "Lender type",
  soft_pull: "Soft-pull pre-qual",
  funding_speed_days: "Funding speed",
  apr_reduction_avg: "Avg APR reduction",
  monthly_savings_avg: "Avg monthly savings",
  payoff_timeline_months: "Typical new term",
  mileage_limit: "Mileage cap",
  transferable: "Transferable",
  cancellation_window_days: "Cancellation window",
  // Iter-150 — Warranty-specific structured fields surfaced in the
  // public spec strip + compare modal so users can scan plan must-knows
  // without expanding the score breakdown.
  waiting_period_days: "Waiting period",
  coverage_max_miles: "Mileage cap",
  // Iter-155 — Phase-3 per-vertical schema completion. Each new key
  // unlocks one of the user-spec'd fields the comparison surface was
  // missing: discount depth (insurance), refinance availability
  // (auto-loan), repair-network breadth (warranty).
  discount_count: "Discounts available",
  refinance_offered: "Refinance support",
  repair_network_size: "Repair network",
};

export const DETAILS_FORMATTERS = {
  avg_monthly_premium: (v) => `$${Math.round(Number(v)).toLocaleString()}/mo`,
  monthly_savings_avg: (v) => `$${Math.round(Number(v)).toLocaleString()}/mo`,
  deductible_default: (v) => `$${Math.round(Number(v)).toLocaleString()} deductible`,
  apr_reduction_avg: (v) => `↓ ${Number(v).toFixed(1)}% APR`,
  payoff_timeline_months: (v) => `${v} mo`,
  funding_speed_days: (v) => (v <= 1 ? "Same-day" : `${v}-day funding`),
  mileage_limit: (v) => `${Number(v).toLocaleString()} mi limit`,
  claim_satisfaction_score: (v) => `${v}/1000 (J.D. Power-style)`,
  cancellation_window_days: (v) => `${v}-day window`,
  soft_pull: (v) => (v ? "Soft pull" : "Hard pull"),
  transferable: (v) => (v ? "Transferable" : "Not transferable"),
  // Iter-150 — Warranty formatters.
  waiting_period_days: (v) =>
    Number(v) <= 0 ? "No waiting period" : `${v}-day wait`,
  coverage_max_miles: (v) =>
    Number(v) >= 500000 ? "No mileage cap" : `${Number(v).toLocaleString()} mi cap`,
  // Iter-155 — Phase-3 formatters.
  discount_count: (v) =>
    Number(v) >= 1 ? `${Number(v)}+ discounts` : "No special discounts",
  refinance_offered: (v) => (v ? "Refi also available" : "Purchase only"),
  repair_network_size: (v) =>
    Number(v) >= 1000
      ? `${Number(v).toLocaleString()}+ repair shops`
      : "Open repair network",
};

// "Glance" prefixes the highest-signal subset of fields per category for
// the public row subhead. Order matters — first three populated values
// render in left→right priority.
// Iter-150 — Warranty glance now exposes 4 must-know specs (coverage tier,
// waiting period, mileage cap, cancellation window) so the user can scan
// plan differences without opening the score breakdown. Falls back
// gracefully if any field is missing.
// Iter-155 — Phase-3 spec strip now also exposes the new
// per-vertical fields (discount_count for insurance, refinance_offered
// for auto-loan, repair_network_size for warranty). The cap stays at
// 4 per category to keep mobile rows scannable.
export const DETAILS_GLANCE_KEYS = {
  insurance: [
    "avg_monthly_premium",
    "deductible_default",
    "coverage_type",
    "discount_count",
  ],
  "auto-loan": [
    "lender_type",
    "funding_speed_days",
    "soft_pull",
    "refinance_offered",
  ],
  refinance: ["apr_reduction_avg", "monthly_savings_avg", "funding_speed_days"],
  warranty: [
    "coverage_type",
    "waiting_period_days",
    "coverage_max_miles",
    "cancellation_window_days",
    "repair_network_size",
  ],
};

// Full ordered key list per category for the side-by-side compare modal +
// expanded breakdown row. Renders fields in editorially-ranked importance.
export const DETAILS_COMPARE_KEYS = {
  insurance: [
    "avg_monthly_premium",
    "deductible_default",
    "coverage_type",
    "claim_satisfaction_score",
    "discount_count",
    "driver_fit",
  ],
  "auto-loan": [
    "lender_type",
    "soft_pull",
    "funding_speed_days",
    "refinance_offered",
  ],
  refinance: [
    "apr_reduction_avg",
    "monthly_savings_avg",
    "payoff_timeline_months",
  ],
  warranty: [
    "coverage_type",
    "waiting_period_days",
    "coverage_max_miles",
    "mileage_limit",
    "deductible_default",
    "transferable",
    "cancellation_window_days",
    "repair_network_size",
  ],
};

export function fmtDetail(key, value) {
  if (value === null || value === undefined || value === "") return null;
  if (DETAILS_FORMATTERS[key]) return DETAILS_FORMATTERS[key](value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

export function detailLabel(key) {
  return DETAILS_LABELS[key] || key.replace(/_/g, " ");
}

// Iter-95 — Winner-highlight directions for the side-by-side compare modal.
// Each key maps to:
//   "lower"  → lowest numeric value wins (premium, APR, funding days)
//   "higher" → highest numeric value wins (savings, mileage cap, score)
//   "true"   → boolean true wins (soft pull available, transferable)
// Keys NOT listed here render neutrally (no winner) — appropriate for
// coverage_type / driver_fit / lender_type where there's no objectively
// better answer.
export const DETAILS_WINNER_DIRECTION = {
  avg_monthly_premium: "lower",
  apr_reduction_avg: "higher",
  monthly_savings_avg: "higher",
  funding_speed_days: "lower",
  claim_satisfaction_score: "higher",
  mileage_limit: "higher",
  cancellation_window_days: "higher",
  soft_pull: "true",
  transferable: "true",
  // Iter-155 — Phase-3 comparable directions for the new schema fields.
  // discount_count → more = better (depth of premium reductions).
  // refinance_offered → boolean; refi-capable lenders win.
  // repair_network_size → more shops = more options for claim time.
  discount_count: "higher",
  refinance_offered: "true",
  repair_network_size: "higher",
};

/**
 * Compute the set of stashed-lender indices that "win" a given detail key.
 * Returns an empty set when the key isn't comparable (neutral text fields)
 * or when fewer than 2 lenders have a value for that key — there's no
 * meaningful winner in a 1-lender comparison.
 *
 * Ties are returned as multiple indices.
 */
export function pickDetailWinners(key, items) {
  const direction = DETAILS_WINNER_DIRECTION[key];
  if (!direction) return new Set();
  const populated = items
    .map((it, idx) => ({ idx, v: it?.details ? it.details[key] : null }))
    .filter(({ v }) => v !== null && v !== undefined && v !== "");
  if (populated.length < 2) return new Set();
  let winners = [];
  if (direction === "true") {
    winners = populated.filter(({ v }) => v === true).map(({ idx }) => idx);
  } else {
    const nums = populated
      .map((p) => ({ idx: p.idx, n: Number(p.v) }))
      .filter(({ n }) => !Number.isNaN(n));
    if (nums.length < 2) return new Set();
    const best =
      direction === "lower"
        ? Math.min(...nums.map((n) => n.n))
        : Math.max(...nums.map((n) => n.n));
    winners = nums.filter(({ n }) => n === best).map(({ idx }) => idx);
  }
  // If everyone ties, surface no winner — visually that just means there's
  // no differentiation, no need to plaster all 3 cells in emerald.
  if (winners.length === items.length) return new Set();
  return new Set(winners);
}

/**
 * Companion for the universal spec rows (apr, rating). Same return shape
 * as pickDetailWinners. Cell highlighter calls both helpers.
 */
export function pickUniversalWinners(specKey, items) {
  if (specKey === "apr") {
    // Use min_apr; ignore rows where it's null.
    const populated = items
      .map((it, idx) => ({ idx, v: it?.min_apr }))
      .filter(({ v }) => v !== null && v !== undefined);
    if (populated.length < 2) return new Set();
    const best = Math.min(...populated.map((p) => Number(p.v)));
    const winners = populated
      .filter((p) => Number(p.v) === best)
      .map((p) => p.idx);
    if (winners.length === items.length) return new Set();
    return new Set(winners);
  }
  if (specKey === "rating") {
    const populated = items
      .map((it, idx) => ({ idx, v: it?.rating }))
      .filter(({ v }) => v !== null && v !== undefined);
    if (populated.length < 2) return new Set();
    const best = Math.max(...populated.map((p) => Number(p.v)));
    const winners = populated
      .filter((p) => Number(p.v) === best)
      .map((p) => p.idx);
    if (winners.length === items.length) return new Set();
    return new Set(winners);
  }
  return new Set();
}
